// CHomeAccountBar — 首頁右上角：金幣、每日簽到、救濟金
// 對應 docs/PRD-Freemium.md 第 4.3 節
//
// 長輩看得清楚的大字；簽到還沒領時按鈕亮起並跳動提醒，救濟金只在金幣不夠時出現。
//
// === 使用圖片 ===
// - public/assets/ui/coin.png（key: COIN_ICON_KEY）— 金幣圖示；未載入時改畫金色圓形
// - 按鈕底：dialog-skin 的 CDialogButtonSurface（cream-button-red／green），詳見該檔頭

import type Phaser from 'phaser';
import type { IAccountSnapshot } from '@net/account-service';
import { createDialogButtonSurface, type CDialogButtonSurface } from './dialog-skin';
import { PAGE_THEME } from './page-skin';

export const COIN_ICON_KEY = 'ui-coin'; // 金幣圖示 asset key

// ---- 版面常數 ----
const BAR_RIGHT_X = 1252; // 金幣條右緣
const BAR_Y = 42; // 金幣條中心 Y
const BAR_WIDTH = 300;
const BAR_HEIGHT = 58;
const BAR_RADIUS = 29;
const BAR_BG = 0x2a1206; // 深咖啡底
const BAR_BG_ALPHA = 0.82;
const BAR_BORDER = 0xe7c26b; // 金邊
const BAR_BORDER_WIDTH = 3;
const COIN_X_OFFSET = 34; // 金幣圖示距左緣
const COIN_SIZE = 44;
const COIN_FALLBACK_COLOR = 0xf2c230;
const COIN_FALLBACK_RIM = 0xa8741a;
const AMOUNT_X_OFFSET = 66; // 數字距左緣
const AMOUNT_FONT_SIZE = 32;
const BUTTON_WIDTH = 140;
const BUTTON_HEIGHT = 56;
const BUTTON_Y = BAR_Y + 70; // 按鈕列中心 Y
const BUTTON_GAP = 14;
const BUTTON_FONT_SIZE = 26;
const DOT_RADIUS = 9; // 可領取紅點
const DOT_COLOR = 0xe53935;
const LINE_BUTTON_Y = BUTTON_Y + 68; // LINE 登入按鈕（簽到按鈕下方）
const BOUNCE_OFFSET = 6; // 提醒跳動幅度
const BOUNCE_MS = 520;

export interface IHomeAccountBarHandlers {
  readonly onSignIn: () => void;
  readonly onRelief: () => void;
  readonly onLineLogin: () => void;
}

interface IBarButton {
  readonly container: Phaser.GameObjects.Container;
  readonly surface: CDialogButtonSurface;
  readonly label: Phaser.GameObjects.Text;
  readonly dot: Phaser.GameObjects.Arc;
  tween: Phaser.Tweens.Tween | null;
}

export class CHomeAccountBar {
  private readonly amount: Phaser.GameObjects.Text;
  private readonly signIn: IBarButton;
  private readonly relief: IBarButton;
  private readonly line: IBarButton;

  constructor(
    private readonly scene: Phaser.Scene,
    handlers: IHomeAccountBarHandlers,
    depth: number
  ) {
    const left = BAR_RIGHT_X - BAR_WIDTH;
    const bg = scene.add.graphics().setDepth(depth);
    bg.fillStyle(BAR_BG, BAR_BG_ALPHA);
    bg.fillRoundedRect(left, BAR_Y - BAR_HEIGHT / 2, BAR_WIDTH, BAR_HEIGHT, BAR_RADIUS);
    bg.lineStyle(BAR_BORDER_WIDTH, BAR_BORDER);
    bg.strokeRoundedRect(left, BAR_Y - BAR_HEIGHT / 2, BAR_WIDTH, BAR_HEIGHT, BAR_RADIUS);
    if (scene.textures.exists(COIN_ICON_KEY)) {
      scene.add
        .image(left + COIN_X_OFFSET, BAR_Y, COIN_ICON_KEY)
        .setDisplaySize(COIN_SIZE, COIN_SIZE)
        .setDepth(depth);
    } else {
      const coin = scene.add.graphics().setDepth(depth);
      coin.fillStyle(COIN_FALLBACK_RIM);
      coin.fillCircle(left + COIN_X_OFFSET, BAR_Y, COIN_SIZE / 2);
      coin.fillStyle(COIN_FALLBACK_COLOR);
      coin.fillCircle(left + COIN_X_OFFSET, BAR_Y, COIN_SIZE / 2 - 4);
    }
    this.amount = scene.add
      .text(left + AMOUNT_X_OFFSET, BAR_Y, '…', {
        color: '#ffe9a8',
        fontFamily: PAGE_THEME.font,
        fontSize: `${AMOUNT_FONT_SIZE}px`,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(depth);
    const signInX = BAR_RIGHT_X - BUTTON_WIDTH / 2;
    const reliefX = signInX - BUTTON_WIDTH - BUTTON_GAP;
    this.signIn = this.createButton(signInX, '每日簽到', handlers.onSignIn, depth);
    this.relief = this.createButton(reliefX, '救濟金', handlers.onRelief, depth);
    this.line = this.createButton(signInX, 'LINE 登入', handlers.onLineLogin, depth);
    this.line.container.setY(LINE_BUTTON_Y);
    this.line.container.setVisible(false);
    this.relief.container.setVisible(false);
    this.signIn.container.setVisible(false);
  }

  update(snapshot: IAccountSnapshot): void {
    this.amount.setText(snapshot.coins.toLocaleString());
    this.setAvailable(this.signIn, !snapshot.signedInToday, true);
    this.setAvailable(this.relief, snapshot.reliefAvailable, false);
  }

  /** LINE 登入按鈕：伺服器開放 LINE 登入、而且這個帳號還沒綁定時才顯示。 */
  setLineLoginVisible(visible: boolean): void {
    this.line.container.setVisible(visible);
  }

  showOffline(): void {
    this.amount.setText('連線中…');
  }

  private setAvailable(button: IBarButton, available: boolean, keepVisible: boolean): void {
    button.container.setVisible(available || keepVisible);
    button.dot.setVisible(available);
    button.surface.setSelected(available);
    button.container.setAlpha(available ? 1 : 0.6);
    if (available && !button.tween) {
      button.tween = this.scene.tweens.add({
        targets: button.container,
        y: BUTTON_Y - BOUNCE_OFFSET,
        duration: BOUNCE_MS,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (!available && button.tween) {
      button.tween.stop();
      button.tween = null;
      button.container.setY(BUTTON_Y);
    }
  }

  private createButton(x: number, text: string, onClick: () => void, depth: number): IBarButton {
    const container = this.scene.add.container(x, BUTTON_Y).setDepth(depth);
    const surface = createDialogButtonSurface(this.scene, 0, 0, BUTTON_WIDTH, BUTTON_HEIGHT);
    const label = this.scene.add
      .text(0, 0, text, {
        color: '#ffffff',
        fontFamily: PAGE_THEME.font,
        fontSize: `${BUTTON_FONT_SIZE}px`,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const dot = this.scene.add.circle(
      BUTTON_WIDTH / 2 - DOT_RADIUS,
      -BUTTON_HEIGHT / 2 + DOT_RADIUS,
      DOT_RADIUS,
      DOT_COLOR
    );
    const hit = this.scene.add
      .zone(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', onClick);
    surface.bindInteraction(hit);
    container.add([surface, label, dot, hit]);
    return { container, surface, label, dot, tween: null };
  }
}
